import { Suspense, lazy } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Navigate, Outlet } from 'react-router';

import { LoadingScreen } from '@zeta-core';
import { RouterLinks } from '../../app-route-paths';
import { AuthGuard } from '../../auth/auth-guard';
import ProtectedRoute from '../../auth/protected-route';
import { CompactErrorPage } from '../../layouts/compact-layout';
import ProfileSetupLayout from '../../layouts/profile-setup-layout';
import { SubscriptionLayout } from '../../layouts/subscription-layout';

const SetupOrganisationProfile = lazy(() => import('../../pages/dashboard/profile-setup/organisation/organisation'));
const SetupMyProfile = lazy(() => import('../../pages/dashboard/profile-setup/my-profile/my-profile'));
const UserManagement = lazy(() => import('../../pages/dashboard/profile-setup/user-management'));
const ChangePassword = lazy(() => import('../../pages/dashboard/profile-setup/change-password'));
const ModelPortfolios = lazy(() => import('../../pages/dashboard/profile-setup/model-portfolios'));
const AdviceTemplates = lazy(() => import('../../pages/dashboard/profile-setup/advice-template'));
const CertificationsAndExpertise = lazy(() => import('../../pages/dashboard/profile-setup/certifications-and-expertise'));
const CertificationsPage = lazy(() => import('../../pages/dashboard/profile-setup/certifications-and-expertise/certifications'));
const ExpertisePage = lazy(() => import('../../pages/dashboard/profile-setup/certifications-and-expertise/expertise'));
const ProfessionalBackgroundPage = lazy(() => import('../../pages/dashboard/profile-setup/certifications-and-expertise/professional-background'));
const WorkingHours = lazy(() => import('../../pages/dashboard/profile-setup/meeting-configuration'));
const RiskAssessment = lazy(() => import('../../pages/dashboard/profile-setup/risk-assessment'));
const FinancialPlanning = lazy(() => import('../../pages/dashboard/profile-setup/financial-planning/financial-planning'));
const FirmProfileIncomplete = lazy(() => import('../../pages/dashboard/profile-setup/firm-profile-incomplete'));
const FactFindConfiguration = lazy(() => import('../../pages/dashboard/profile-setup/fact-find-configuration'));
const BillingAndInvoice = lazy(() => import('../../pages/dashboard/profile-setup/billing-and-invoice'));
const InvoiceDetail = lazy(() => import('../../pages/dashboard/profile-setup/invoice-detail'));
const DocumentManagement = lazy(() => import('../../pages/dashboard/profile-setup/document-management'));
const FeesAndChargesManagement = lazy(() => import('../../pages/dashboard/profile-setup/fee-and-charges-management'));
const ClientCommunication = lazy(() => import('../../pages/dashboard/profile-setup/client-communication'));
const CRMPage = lazy(() => import('../../pages/dashboard/profile-setup/crm'));

const ProfileSetupRoutes = [
  {
    path: '/profile-setup',
    element: (
      <ErrorBoundary FallbackComponent={CompactErrorPage}>
        <Suspense fallback={<LoadingScreen />}>
          <AuthGuard>
            <ProtectedRoute>
              <ProfileSetupLayout>
                <Outlet />
              </ProfileSetupLayout>
            </ProtectedRoute>
          </AuthGuard>
        </Suspense>
      </ErrorBoundary>
    ),

    children: [
      { index: true, element: <Navigate to={RouterLinks.organisationProfile} replace /> },
      { path: 'organisation-profile', element: <SetupOrganisationProfile /> },
      { path: 'my-profile', element: <SetupMyProfile /> },
      { path: 'user-management', element: <UserManagement /> },
      { path: 'change-password', element: <ChangePassword /> },
      {
        path: 'model-portfolios',
        element: (
          <SubscriptionLayout requiredFeature="MODEL_PORTFOLIO">
            <ModelPortfolios />
          </SubscriptionLayout>
        ),
      },
      { path: 'risk-assessment', element: <RiskAssessment /> },
      { path: 'financial-planning', element: <FinancialPlanning /> },

      { path: 'advice-templates', element: <AdviceTemplates /> },
      { path: 'document-management', element: <DocumentManagement /> },
      { path: 'fees-and-charges-management', element: <FeesAndChargesManagement /> },
      { path: 'fact-find-configuration', element: <FactFindConfiguration /> },
      { path: 'client-communication', element: <ClientCommunication /> },
      { path: 'crm', element: <CRMPage /> },
      { path: 'products', element: <Navigate to={RouterLinks.crm} replace /> },
      {
        path: 'certifications-and-expertise',
        element: <CertificationsAndExpertise />,
        children: [
          { index: true, element: <Navigate to={RouterLinks.certificationsAndExpertiseCert} replace /> },
          { path: 'certifications', element: <CertificationsPage /> },
          { path: 'expertise', element: <ExpertisePage /> },
          { path: 'professional-background', element: <ProfessionalBackgroundPage /> },
        ],
      },
      { path: 'meeting-configuration', element: <WorkingHours /> },
      {
        path: 'billing-and-invoice',
        element: <BillingAndInvoice />,
      },
      {
        path: 'invoice-detail',
        element: <InvoiceDetail />,
      },
      { path: '*', element: <Navigate to={RouterLinks.organisationProfile} /> },
    ],
  },
  {
    path: 'firm-profile-incomplete',
    element: (
      <ErrorBoundary FallbackComponent={CompactErrorPage}>
        <Suspense fallback={<LoadingScreen />}>
          <AuthGuard>
            <FirmProfileIncomplete />
          </AuthGuard>
        </Suspense>
      </ErrorBoundary>
    ),
  },
];
export default ProfileSetupRoutes;
